import React from 'react';
import { heatStyle } from '../../../utils/styleHelpers';
import styles from './CupBoard.module.css';

/**
 * Reusable cup board for stats display (not game-in-progress).
 *
 * Props:
 *   attackData   { bk, ct, lf, rt, fr }  – hit-rate numbers (0-1) for the top side
 *   defenseData  { fr, rt, lf, ct, bk }  – hit-rate numbers (0-1) for the bottom side
 *   variant      "full" | "mini" | "compact"
 *   showHeat     bool – render heat-map gradients (ignored for "mini")
 *   topType      "AT" | "DF"  (default "AT") – cup type for top side (affects mini fill colour)
 *   bottomType   "AT" | "DF"  (default "DF") – cup type for bottom side
 *   attackLabels  { bk, ct, lf, rt, fr }  – optional tooltip text overrides for top side
 *   defenseLabels { fr, rt, lf, ct, bk }  – optional tooltip text overrides for bottom side
 */
const CupBoard = ({
    attackData = {},
    defenseData = {},
    variant = 'full',
    showHeat = false,
    topType = 'AT',
    bottomType = 'DF',
    attackLabels,
    defenseLabels,
}) => {
    const isMini = variant === 'mini';

    const miniFillClass = (value) => {
        if (!value || value < 0.1) return styles.fillEmpty;
        if (value >= 0.9) return styles.fillFull;
        return styles.fillHalf;
    };

    const renderCup = (type, pos, value, labelOverride) => {
        const displayVal = labelOverride ?? (value != null ? Number(value).toFixed(2) : '0.00');
        if (isMini) {
            return (
                <div
                    className={`${styles.cup} ${styles.cupMini} ${miniFillClass(value)}`}
                    data-cuptype={type}
                    data-position={pos.toUpperCase()}
                    data-value={displayVal}
                />
            );
        }
        return (
            <div
                className={`${styles.cup} ${styles.cupFull}`}
                data-cuptype={type}
                data-position={pos.toUpperCase()}
                data-value={displayVal}
                style={showHeat ? heatStyle(value || 0, type) : undefined}
            />
        );
    };

    const boardClass = isMini
        ? `${styles.board} ${styles.boardMini}`
        : `${styles.board} ${styles.boardFull}`;
    const rectClass = isMini
        ? `${styles.innerRect} ${styles.innerRectMini}`
        : `${styles.innerRect} ${styles.innerRectFull}`;

    return (
        <div className={boardClass}>
            <div className={rectClass}>
                {/* Top side */}
                <div className={styles.side}>
                    <div className={styles.cupFormation}>
                        <div className={styles.cupRow}>
                            {renderCup(topType, 'BK', attackData.bk, attackLabels?.bk)}
                        </div>
                        <div className={styles.cupRow}>
                            {renderCup(topType, 'CT', attackData.ct, attackLabels?.ct)}
                        </div>
                        <div className={styles.cupRow}>
                            {renderCup(topType, 'LF', attackData.lf, attackLabels?.lf)}
                            {renderCup(topType, 'RT', attackData.rt, attackLabels?.rt)}
                        </div>
                        <div className={styles.cupRow}>
                            {renderCup(topType, 'FR', attackData.fr, attackLabels?.fr)}
                        </div>
                    </div>
                </div>

                <div className={styles.divider} />

                {/* Bottom side */}
                <div className={styles.side}>
                    <div className={styles.cupFormation}>
                        <div className={styles.cupRow}>
                            {renderCup(bottomType, 'FR', defenseData.fr, defenseLabels?.fr)}
                        </div>
                        <div className={styles.cupRow}>
                            {renderCup(bottomType, 'RT', defenseData.rt, defenseLabels?.rt)}
                            {renderCup(bottomType, 'LF', defenseData.lf, defenseLabels?.lf)}
                        </div>
                        <div className={styles.cupRow}>
                            {renderCup(bottomType, 'CT', defenseData.ct, defenseLabels?.ct)}
                        </div>
                        <div className={styles.cupRow}>
                            {renderCup(bottomType, 'BK', defenseData.bk, defenseLabels?.bk)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CupBoard;
